/**
 * Analytics Widgets Hooks
 *
 * React Query hooks for dashboard analytics widgets
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analyticsService, BestTimesData, AIInsightsData } from '@/services/analytics.service';

// Query keys
export const analyticsWidgetKeys = {
  bestTimes: (platform?: string) => ['analytics', 'best-times', platform] as const,
  aiInsights: (platform?: string) => ['analytics', 'ai-insights', platform] as const,
  summary: (from: string, to: string) => ['analytics', 'summary', from, to] as const,
};

/**
 * Hook to fetch best posting times
 */
export function useBestTimes(platform?: string) {
  return useQuery<BestTimesData[], Error>({
    queryKey: analyticsWidgetKeys.bestTimes(platform),
    queryFn: () => analyticsService.getBestTimes(platform),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch AI insights.
 *
 * `refetchInterval` lets callers gate polling on a local "generating" flag
 * (e.g. while an AI insights generation job is in flight) without affecting
 * the default staleTime-based caching behavior.
 */
export function useAIInsights(platform?: string, refetchInterval: number | false = false) {
  return useQuery<AIInsightsData[], Error>({
    queryKey: analyticsWidgetKeys.aiInsights(platform),
    queryFn: () => analyticsService.getAIInsights(platform),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval,
  });
}

/**
 * Hook to generate AI insights.
 *
 * The mutation itself is intentionally "dumb" — it only performs the POST.
 * Callers (widgets) must branch on `status` ('queued' | 'insufficient_data')
 * in their own `onSuccess`/`onError` handlers to drive polling, messaging,
 * and the generating/spinner state, since a single default `onSuccess` can't
 * express that branching. Query invalidation isn't needed here either: the
 * `queued` case is followed by `useAIInsights` polling via `refetchInterval`.
 */
export function useGenerateAIInsights() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: analyticsService.generateAIInsights,
    onSuccess: (result) => {
      // For the `insufficient_data` case there's nothing new to fetch, but a
      // refetch is cheap and keeps the cache honest if postsFound changed.
      if (result.status === 'queued') {
        queryClient.invalidateQueries({ queryKey: ['analytics', 'ai-insights'] });
      }
    },
  });
}
